/**
 * Season-aware helper functions for validation and organization
 */
import { Event } from '../types';
import { getSeasonMonths, isMonthInSeason, parseSeasonId } from './seasonDomain';

export { parseSeasonId } from './seasonDomain';

const MAX_EVENT_NAME_LENGTH = 100;

export function getValidMonthsForSeason(seasonName: string): string[] {
    return [...getSeasonMonths(seasonName)];
}

export function validateEventForSeason(
    eventData: { name: string; month: string },
    seasonContext: { seasonName: string; year: number },
    existingEvents?: Event[],
    excludeId?: number
): { isValid: boolean; error?: string } {
    const trimmedName = eventData.name?.trim() ?? '';
    if (!trimmedName) {
        return { isValid: false, error: 'Event name is required' };
    }

    if (!eventData.month) {
        return { isValid: false, error: 'Event month is required' };
    }

    if (trimmedName.length > MAX_EVENT_NAME_LENGTH) {
        return { isValid: false, error: `Event name must be ${MAX_EVENT_NAME_LENGTH} characters or less` };
    }

    if (existingEvents) {
        const normalizedName = trimmedName.toLowerCase();
        const duplicateExists = existingEvents.some(event => {
            const nameConflicts = event.name.toLowerCase() === normalizedName;
            const isOtherEvent = excludeId ? event.id !== excludeId : true;
            return nameConflicts && isOtherEvent;
        });

        if (duplicateExists) {
            return { isValid: false, error: 'An event with this name already exists' };
        }
    }

    if (!isMonthInSeason(eventData.month, seasonContext.seasonName)) {
        const validMonths = getValidMonthsForSeason(seasonContext.seasonName);
        return {
            isValid: false,
            error: `Month must be one of: ${validMonths.join(', ')} for ${seasonContext.seasonName}`
        };
    }

    return { isValid: true };
}

/** "Name Year" for a season id; unparseableLabel (default: the raw id) for a malformed one. */
export function formatSeasonDisplay(seasonId: string, unparseableLabel: string = seasonId): string {
    const parsed = parseSeasonId(seasonId);
    if (!parsed) {
        return unparseableLabel;
    }

    return `${parsed.name} ${parsed.year}`;
}

export function calculateSeasonStats(events: Record<string, { photos: any[] }>): { eventCount: number; photoCount: number } {
    const eventCount = Object.keys(events).length;
    const photoCount = Object.values(events).reduce((sum, event) => sum + event.photos.length, 0);
    return { eventCount, photoCount };
}
